import { getSettingValue } from './settingsStorefront';

const parsePercent = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
};

const normalizeState = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export function calculateLineTotal(item) {
  const price = Number(item?.price ?? 0);
  const quantity = Number(item?.quantity ?? 0);
  return price * quantity;
}

export function calculateCartSubtotal(items = []) {
  return items.reduce((sum, item) => sum + calculateLineTotal(item), 0);
}

export function calculateCartOriginalSubtotal(items = []) {
  return items.reduce((sum, item) => {
    const price = Number(item?.originalPrice ?? item?.price ?? 0);
    const quantity = Number(item?.quantity ?? 0);
    return sum + price * quantity;
  }, 0);
}

/** Bundle discount from group price — only when full bundle still in cart. */
export function calculateBundleDiscount(items = []) {
  const groups = new Map();

  items.forEach((item) => {
    if (!item?.isBundleItem || !item?.bundleGroupId) return;
    if (item.bundleGroupComplete !== true) return;

    const key = String(item.bundleGroupId);
    if (!groups.has(key)) {
      groups.set(key, { lineSum: 0, originalSum: 0, bundlePrice: null });
    }

    const group = groups.get(key);
    group.lineSum += calculateLineTotal(item);
    group.originalSum += Number(item.originalPrice ?? item.price ?? 0) * Number(item.quantity ?? 0);

    const bundleGroupPrice = item.bundleGroupPrice;
    if (bundleGroupPrice != null && bundleGroupPrice !== '') {
      group.bundlePrice = Number(bundleGroupPrice);
    }
  });

  let discount = 0;
  groups.forEach((group) => {
    if (group.bundlePrice != null && group.bundlePrice > 0) {
      discount += Math.max(0, group.originalSum - group.bundlePrice);
    } else if (group.originalSum > group.lineSum) {
      discount += group.originalSum - group.lineSum;
    }
  });

  return Math.round(discount * 100) / 100;
}

/** Cart / pre-address — no tax; subtotal equals grand total. */
export function calculateCartTotals(items = []) {
  const originalSubtotal = calculateCartOriginalSubtotal(items);
  const bundleDiscount = calculateBundleDiscount(items);
  const subtotal = Math.round((originalSubtotal - bundleDiscount) * 100) / 100;

  return {
    subtotal,
    originalSubtotal,
    bundleDiscount,
    taxAmount: 0,
    taxLines: [],
    gstPercent: 0,
    cstPercent: 0,
    igstPercent: 0,
    gstAmount: 0,
    cstAmount: 0,
    igstAmount: 0,
    taxType: null,
    grandTotal: subtotal,
  };
}

/**
 * After delivery address — GST+CST (same state) or IGST (different state).
 * Business state from Settings → BusinessState (seller).
 */
export function calculateOrderTotals(items = [], settings = {}, address = null) {
  const base = calculateCartTotals(items);
  const subtotal = base.subtotal;
  const customerState = address?.state ?? address?.State ?? '';

  if (!String(customerState).trim()) {
    return base;
  }

  const businessState = getSettingValue(settings, 'BusinessState', '');
  const gstPercent = parsePercent(getSettingValue(settings, 'GST', 0));
  const cstPercent = parsePercent(getSettingValue(settings, 'CST', 0));
  const igstPercent = parsePercent(getSettingValue(settings, 'IGST', 0));

  const isInterState =
    Boolean(String(businessState).trim()) &&
    normalizeState(customerState) !== normalizeState(businessState);

  if (isInterState) {
    const igstAmount = subtotal * (igstPercent / 100);
    return {
      ...base,
      subtotal,
      taxAmount: igstAmount,
      taxLines: igstPercent > 0 ? [{ key: 'igst', label: `IGST (${igstPercent}%)`, amount: igstAmount }] : [],
      gstPercent: 0,
      cstPercent: 0,
      igstPercent,
      gstAmount: 0,
      cstAmount: 0,
      igstAmount,
      taxType: 'IGST',
      grandTotal: subtotal + igstAmount,
    };
  }

  const gstAmount = subtotal * (gstPercent / 100);
  const cstAmount = subtotal * (cstPercent / 100);
  const taxLines = [];

  if (gstPercent > 0) {
    taxLines.push({ key: 'gst', label: `GST (${gstPercent}%)`, amount: gstAmount });
  }
  if (cstPercent > 0) {
    taxLines.push({ key: 'cst', label: `CST (${cstPercent}%)`, amount: cstAmount });
  }

  return {
    ...base,
    subtotal,
    taxAmount: gstAmount + cstAmount,
    taxLines,
    gstPercent,
    cstPercent,
    igstPercent: 0,
    gstAmount,
    cstAmount,
    igstAmount: 0,
    taxType: 'GST_CST',
    grandTotal: subtotal + gstAmount + cstAmount,
  };
}
